import { AssociationType } from '@libs/associations';
import type { ProductAttribute } from '@libs/attribute.helper';
import { getAttributeValue, getAttributeValueAsString } from '@libs/attribute.helper';
import { getPriceDisplayTypeForOrderType } from '@libs/product-price-utils';
import { getAssociationProductReferences, getProductIdForLocale } from '@libs/product.helper';
import type { WidenMediaElement } from '@libs/widen-utils';
import type {
  AssociationIbhProductFragmentEnhanced,
  AssociationProductFragmentEnhanced,
  ProductFragmentEnhanced,
} from '@models/enhanced-fragments';
import { ProductHelper } from '@models/graphql';
import type { SuitableProductProps } from '@product/SuitableProduct/SuitableProduct';

export const getSuitableProducts = (
  product: ProductFragmentEnhanced,
  associationProducts: Record<string, AssociationProductFragmentEnhanced | AssociationIbhProductFragmentEnhanced>,
  locale: string,
  legacy = false
): SuitableProductProps[] => {
  const associationProductRefs = getAssociationProductReferences(product, [
    AssociationType.SUITABLE_PRODUCT,
    AssociationType.MOUNTING_BRACKETS_TRIFLEX,
    AssociationType.ACCESSORIES,
  ]);

  if (!associationProducts || !associationProductRefs) {
    return [];
  }

  return (
    associationProductRefs
      // Filter out all invalid entries
      .map(suitableProductRef => associationProducts[suitableProductRef.id])
      .filter(associatedProduct => !!associatedProduct)
      .map(associatedProduct => {
        const {
          id: productId,
          model_id,
          aggregated_product_prices,
          delivery_times: deliveryTimes,
        } = associatedProduct as AssociationProductFragmentEnhanced;

        const title: string = getAttributeValueAsString(
          associatedProduct.attributes?.series_description as ProductAttribute<string>
        );
        const slug: string = model_id.replace(/\//g, '');
        const image: WidenMediaElement | null =
          ProductHelper.getTeaserImage(associatedProduct as AssociationProductFragmentEnhanced) || null;

        // These two properties defined as any and cause errors by `next lint` in jenkins
        // it refuses to destruct them as any type
        const usp: string = getAttributeValueAsString(
          associatedProduct.attributes?.attr_USP as ProductAttribute<string>
        );
        const isNew = Boolean(
          getAttributeValue<boolean>(
            associatedProduct.attributes?.attr_status_new_product_boolean as ProductAttribute<boolean>
          )
        );

        const priceType = getPriceDisplayTypeForOrderType(
          getAttributeValue<string>(associatedProduct.attributes?.order_type as ProductAttribute<string>)
        );

        const firstAggregatedPrice = aggregated_product_prices?.[0];
        const prices = firstAggregatedPrice?.prices ? firstAggregatedPrice.prices : null;

        const { articleId, productCmsId } = associatedProduct as AssociationIbhProductFragmentEnhanced;

        let legacyLinkTarget: string | null = null;
        if (legacy && productCmsId && articleId) {
          legacyLinkTarget = `/product/${productCmsId}?artNr=${articleId}`;
        }

        return {
          title,
          slug,
          usp,
          new: isNew,
          image,
          productId,
          priceType,
          prices,
          deliveryTimes: deliveryTimes || null,
          primaryCategoryCode:
            getAttributeValue<string>(associatedProduct.attributes.main_category_key as ProductAttribute<string>) ||
            null,
          productIdDisplayName: getProductIdForLocale(associatedProduct, locale),
          legacyLinkTarget,
        };
      })
  );
};
